import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { userStore } from '../user.store';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  private router = inject(Router);

  readonly store = inject(userStore);

  constructor() {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const app = route.url[0]?.path;
    const url = state.url;
    const lastUrl = sessionStorage.getItem('lastUrl');

    return forkJoin([this.store.setUserInfos()!, this.store.setAccessApp()!]).pipe(
      map(() => {
        // 🔐 1. AUTH CHECK
        if (!this.store.isAuthentified()) {
          this.router.navigateByUrl('/');
          return false;
        }

        const user = this.store.user();
        const access = this.store.access_app();

        // 🔁 2. REDIRECTIONS GLOBALES
        if (url.includes('activation') || url.includes('reinitialisation') || url === '/') {
          this.router.navigateByUrl('/mon-portail');
          return true;
        }

        if (user.in_bilat === 'true' && !url.includes('quotidien') && !url.includes('cahier-de-liaison')) {
          this.router.navigateByUrl('/quotidien');
          return true;
        }

        // 🛡️ 3. CHECK ACCESS APP (si route avec app)
        if (app) {
          const checkAccess = (name: string, routeKey: string, trackNav = true) => {
            const hasAccess = access.some((a) => a.name_application === name);
            if (!hasAccess) {
              this.router.navigateByUrl('/');
              return false;
            }
            const appId = access.find((a) => a.name_application === name)?.id_application;

            this.store.getAlertDev(appId!);

            if (trackNav && !lastUrl?.includes(routeKey)) {
              this.store.postUserNavigationHistory({
                id_user_account: user.id,
                id_application: appId!,
              });
            }

            return true;
          };

          switch (app) {
            case 'cahier-de-liaison':
              return checkAccess('cahier de liaison', 'cahier-de-liaison');
            case 'outils-marketing':
              return checkAccess('outils marketing', 'outils-marketing', !lastUrl);
            case 'vd':
              return checkAccess('vd', 'vd');
            case 'essai-pret':
              return checkAccess('essai-pret', 'essai-pret');
            case 'post-liv':
              return checkAccess('post liv', 'post-liv');
            case 'formation':
              return checkAccess('formation', 'formation');
            case 'requeteur':
              return checkAccess('requeteur', 'requeteur');
            case 'recrutement':
              return checkAccess('recrutement', 'recrutement');
            case 'quotidien':
              return checkAccess('quotidien', 'quotidien');
            case 'campagne':
              return checkAccess('campagne', 'campagne');
            case 'appel-apv':
              return checkAccess('appel-apv', 'appel-apv');
            case 'vo':
              return checkAccess('vo', 'vo');
            case 'objectifs':
              return checkAccess('objectifs prime', 'objectifs');
            case 'optiliv':
              return checkAccess('optiliv', 'optiliv');
            case 'carriere':
              return checkAccess('carriere +', 'carriere');
            case 'perfco':
              return checkAccess('perf Co', 'perfco');
            case 'marge':
              return checkAccess('marge 2', 'marge');
            case 'cockpit':
              return checkAccess('cockpit', 'cockpit');
            case 'suivi-creance':
              return checkAccess('créances', 'suivi-creance');
            case 'piilot':
              return checkAccess('piilot', 'piilot');
            case 'plan-de-lancement':
              return checkAccess('plan de lancement', 'plan-de-lancement');
            case 'portail':
            case 'calendrier':
            case 'admin-check':
            case 'profile':
              return true;
            case 'recrutement-formation':
              return checkAccess('recrutement formation', 'recrutement-formation');
            case 'easy-frais':
              return checkAccess('easy frais', 'easy-frais');
            case 'or-snap':
              return checkAccess('or snap', 'or-snap');
            case 'suivi-prime':
              return true;
            case 'siv-tracker':
              return checkAccess('siv tracker', 'siv-tracker');
            default:
              this.router.navigateByUrl('/');
              return false;
          }
        }

        return true;
      }),
      catchError(() => {
        this.router.navigateByUrl('/');
        return of(false);
      }),
    );
  }
}
